import React from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { array, number, object, string } from "yup";
import { showToast } from "src/utils/showToaster";
import DrawerExchangeForm from "../Layouts/DrawerExchangeForm";
import { DrawerExchangeFormValue } from "src/models/Drawer.model";
import {
  useDrawerExchangeMutation,
  useGetAllDrawerDataQuery,
} from "src/services/DrawerService";
import SideNavLayout from "src/components/layouts/SideNavLayout/SideNavLayout";
import { useNavigate } from "react-router-dom";

const DrawerExchangeFormWrapper = () => {
  const { data } = useGetAllDrawerDataQuery("");
  const physicalModes =
    data?.data?.filter((mode: any) => mode?.type === "PHYSICAL") || [];
  const navigate = useNavigate();
  // Form Initial Values
  const initialValues: DrawerExchangeFormValue = {
    inflow: physicalModes.map((mode: any) => ({
      name: mode.name,
      value: mode.value,
      quantity: "", // for user input
      type: mode.type,
      availableQuantity: mode.quantity, // API "quantity" becomes availableQuantity
    })),
    outflow: physicalModes.map((mode: any) => ({
      name: mode.name,
      value: mode.value,
      quantity: "", // for user input
      type: mode.type,
      availableQuantity: mode.quantity, // API "quantity" becomes availableQuantity
    })),
    reason: "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    reason: string().required("Please enter reason"),
    outflow: array()
      .of(
        object().shape({
          name: string().required(),
          value: number().required().positive(),
          quantity: string().when("availableQuantity", {
            is: (val: number) => val > 0,
            then: (schema) =>
              schema.test(
                "not-exceed",
                "Cannot exceed available quantity",
                function (value) {
                  const available = this.parent.availableQuantity || 0;
                  return Number(value || 0) <= available;
                }
              ),
            otherwise: (schema) => schema.notRequired(),
          }),
          type: string().required().oneOf(["PHYSICAL"]),
        })
      )
      .min(1, "At least one outflow item is required"),
  });

  const [drawerExchange] = useDrawerExchangeMutation();

  const handleSubmit = async (
    values: DrawerExchangeFormValue,
    { setSubmitting, resetForm }: FormikHelpers<DrawerExchangeFormValue>
  ) => {
    try {
      // Calculate totals
      const totalInflow = values.inflow.reduce((acc, curr) => {
        return acc + (curr.quantity || 0) * (curr.value || 0);
      }, 0);
      const filteredOutflow = values.outflow.filter(
        (item) => item !== undefined
      );
      const totalOutflow = filteredOutflow.reduce((acc, curr) => {
        return acc + (curr?.quantity || 0) * (curr?.value || 0);
      }, 0);
      if (totalInflow !== totalOutflow) {
        showToast("error", "Total Inflow and Outflow must be equal.");
        return;
      }

      // Format values for API
      const formattedValues = {
        inflow: values.inflow.map((item) => ({
          name: item.name,
          value: item.value,
          type: item.type,
          quantity: Number(item.quantity),
        })),
        outflow: values.outflow.map((item) => ({
          name: item.name,
          value: item.value,
          type: item.type,
          quantity: Number(item.quantity),
        })),
        reason: values.reason,
      };
      const res: any = await drawerExchange(formattedValues);

      if (res?.error) {
        showToast("error", res?.error?.data?.message || "Something went wrong");
      } else if (res?.data?.status) {
        showToast("success", res?.data?.message);
        navigate("/drawer");
        resetForm();
      } else {
        showToast("error", res?.data?.message || "Failed");
      }
    } catch (err) {
      showToast("error", "Unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          <SideNavLayout>
            <DrawerExchangeForm formikProps={formikProps} />
          </SideNavLayout>
        </Form>
      )}
    </Formik>
  );
};

export default DrawerExchangeFormWrapper;
